import React, { useEffect, useState } from "react";
import {
  Form,
  Select,
  InputNumber,
  Button,
  Space,
  Typography,
  Modal,
} from "antd";
import { getInventarioNoSerial } from "../../../services/inventarioNoSerial";
import { readItems } from "@directus/sdk";
import { getDirectusClient } from "../../../lib/directus";

const { Option } = Select;

function AddNoSerial({ onAddItem, productosagregados = [] }) {
  const [inventarioItems, setInventarioItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [availableQuantity, setAvailableQuantity] = useState(null);

  const fetchInventarioItems = async () => {
    setLoading(true);
    try {
      const items = await getInventarioNoSerial();
      setInventarioItems(items);
    } catch (error) {
      console.error("Error fetching inventario_no_serial items:", error);
      Modal.error({
        title: "Error",
        content: "Error al cargar los items de inventario no serializado",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventarioItems();
  }, []);

  useEffect(() => {
    const selectedItemId = form.getFieldValue("item");
    const selectedItem = inventarioItems.find(
      (item) => item.id === selectedItemId
    );

    if (selectedItem) {
      const fetchAvailable = async () => {
        try {
          const totalInventarioQuantity = Number(selectedItem.cantidad) || 0;

          const directus = await getDirectusClient();
          const prestamoItems = await directus.request(
            readItems("inventario_ns_prestamo", {
              fields: ["cantidad"],
              filter: {
                inventario_ns: { _eq: selectedItemId },
                estado: { _in: ["prestado", "solicitud_prestamo"] },
              },
              limit: -1,
            })
          );

          let totalAllocatedQuantity = 0;
          if (prestamoItems && prestamoItems.length > 0) {
            totalAllocatedQuantity = prestamoItems.reduce(
              (sum, item) => sum + (Number(item.cantidad) || 0),
              0
            );
          }

          // Verifica que productosagregados exista antes de usar .filter()
          const cantidadAgregada = (productosagregados || [])
            .filter((item) => item.id === selectedItemId)
            .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

          const trulyAvailable =
            totalInventarioQuantity - totalAllocatedQuantity - cantidadAgregada;
          setAvailableQuantity(trulyAvailable >= 0 ? trulyAvailable : 0);
        } catch (error) {
          console.error("Error fetching available quantity:", error);
          setAvailableQuantity(0);
        }
      };
      fetchAvailable();
    } else {
      setAvailableQuantity(null);
    }
  }, [form, form.getFieldValue("item"), inventarioItems, productosagregados]);

  const onFinish = async (values) => {
    console.log("onFinish values:", values);
    const selectedItem = inventarioItems.find(
      (item) => item.id === values.item
    );

    if (!selectedItem) {
      console.error("No item selected.");
      return;
    }

    const quantityToAdd = Number(values.quantity);

    const errors = form.getFieldsError(["item", "quantity"]);
    const hasErrors = errors.some(({ errors }) => errors.length > 0);

    if (hasErrors) {
      console.log("Form has validation errors:", errors);
      return;
    }

    if (availableQuantity !== null && quantityToAdd > availableQuantity) {
      form.setFields([
        {
          name: "quantity",
          errors: [`Solo hay ${availableQuantity} disponibles para préstamo!`],
        },
      ]);
      return;
    }

    if (onAddItem) {
      onAddItem({ ...selectedItem, quantity: quantityToAdd });
      form.resetFields();
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Space align="end">
        <Form.Item
          name="item"
          label="Producto no serializado"
          rules={[
            { required: true, message: "Por favor selecciona un producto!" },
          ]}
          style={{ width: 300 }}
        >
          <Select
            placeholder="Selecciona un item"
            loading={loading}
            showSearch
            filterOption={(input, option) =>
              String(option.children)
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            onChange={async (selectedItemId) => {
              form.setFieldsValue({ item: selectedItemId });
              if (selectedItemId) {
                const selectedItem = inventarioItems.find(
                  (item) => item.id === selectedItemId
                );
                if (selectedItem) {
                  const totalInventarioQuantity =
                    Number(selectedItem.cantidad) || 0;

                  const directus = await getDirectusClient();
                  const prestamoItems = await directus.request(
                    readItems("inventario_ns_prestamo", {
                      fields: ["cantidad"],
                      filter: {
                        inventario_ns: { _eq: selectedItemId },
                        estado: { _in: ["prestado", "solicitud_prestamo"] },
                      },
                      limit: -1,
                    })
                  );

                  let totalAllocatedQuantity = 0;
                  if (prestamoItems && prestamoItems.length > 0) {
                    totalAllocatedQuantity = prestamoItems.reduce(
                      (sum, item) => sum + (Number(item.cantidad) || 0),
                      0
                    );
                  }

                  const trulyAvailable =
                    totalInventarioQuantity - totalAllocatedQuantity;
                  setAvailableQuantity(
                    trulyAvailable >= 0 ? trulyAvailable : 0
                  );
                }
              }
            }}
          >
            {inventarioItems.map((item) => (
              <Option key={item.id} value={item.id}>
                {item.nombre}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="quantity"
          label="Cantidad"
          rules={[
            { required: true, message: "Por favor ingresa la cantidad!" },
            { type: "number", message: "Por favor ingresa un número válido" },
            {
              type: "number",
              min: 1,
              message: "La cantidad debe ser al menos 1",
            },
          ]}
          style={{ width: 120 }}
        >
          <InputNumber
            min={1}
            onChange={async (value) => {
              form.setFieldsValue({ quantity: value });

              const selectedItemId = form.getFieldValue("item");
              const numericValue = Number(value);

              if (
                selectedItemId &&
                Number.isFinite(numericValue) &&
                numericValue >= 1
              ) {
                try {
                  if (numericValue > availableQuantity) {
                    form.setFields([
                      {
                        name: "quantity",
                        errors: [
                          `Solo hay ${availableQuantity} disponibles para prestamo!`,
                        ],
                      },
                    ]);
                  } else {
                    form.setFields([
                      {
                        name: "quantity",
                        errors: [],
                      },
                    ]);
                  }
                } catch (error) {
                  console.error("Error during onChange validation:", error);
                  form.setFields([
                    {
                      name: "quantity",
                      errors: ["Error validando la cantidad."],
                    },
                  ]);
                }
              } else {
                form.setFields([
                  {
                    name: "quantity",
                    errors: [],
                  },
                ]);
              }
            }}
          />
        </Form.Item>
        {form.getFieldValue("item") && (
          <Typography.Text>
            {availableQuantity !== null
              ? `Disponible para prestamo: ${availableQuantity}`
              : "No hay cantidad disponible para prestamo para este producto."}
          </Typography.Text>
        )}
        <Form.Item>
          <Button type="primary" htmlType="submit">
            Agregar
          </Button>
        </Form.Item>
      </Space>
    </Form>
  );
}

export default AddNoSerial;
